import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { IAllFilterData, ILogRowData } from "../utils";
import _ from "lodash";
import { mergeClass, removeNbsp, removeTags } from "../../../utils";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { chartColors } from "../config";

export interface IPieChartProps {
  groupBy: keyof IAllFilterData;
  displayData: ILogRowData[];
  filterData: IAllFilterData;
}

export interface IChartGroup {
  name: string;
  value: number;
  link?: string;
  display_name?: string;
}

interface IReducedChartGroup {
  normal: IChartGroup[];
  reduced: IChartGroup[];
}

const hlColor = "#455163";

const BasePieChart = ({
  data,
  combineMinimum = true,
}: {
  data: { name: string; value: number }[];
  combineMinimum?: boolean;
}) => {
  const [highlight, setHighlight] = useState<number | string | null>(null);
  const [showReduced, setShowReduced] = useState<boolean>(true);
  const [containerWidth, setContainerWidth] = useState<number>(540);
  const updateContainerWidth = () => {
    const pieContainer = document.getElementById("pie_chart_container");
    if (pieContainer) setContainerWidth(pieContainer?.offsetWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", _.debounce(updateContainerWidth, 20));
    return () =>
      window.removeEventListener(
        "resize",
        _.debounce(updateContainerWidth, 20)
      );
  }, []);

  useEffect(() => {
    updateContainerWidth();
  }, [data]);

  const Label = ({ x, y, display_name, percent }: any) => {
    const alignLeft = x <= containerWidth * 0.5 ? 1 : -1;
    const textAnchor = alignLeft > 0 ? "end" : "start";
    return (
      <text
        x={x - alignLeft * 3}
        y={y + 4}
        fontSize={"0.72rem"}
        textAnchor={textAnchor}
        fill={display_name === highlight ? hlColor : undefined}
      >{`${_.ceil(percent * 100, 1)}%`}</text>
    );
  };

  const length = 50; // max display name length

  // Data sorting
  data.sort((a, b) => b.value - a.value);
  const splitData = data.reduce(
    (arr: IReducedChartGroup, item: IChartGroup, index: number) => {
      const display_name = _.truncate(removeNbsp(removeTags(item.name)), {
        length,
      });
      if (index > 9)
        return { ...arr, reduced: [...arr.reduced, { ...item, display_name }] };
      else
        return {
          ...arr,
          normal: [...arr.normal, { ...item, display_name }],
        };
    },
    { normal: [], reduced: [] }
  );
  const hasReducedData = splitData.reduced.length > 1;
  const useThisData =
    !hasReducedData || !showReduced
      ? data.map((d) => {
          const display_name = _.truncate(removeNbsp(removeTags(d.name)), {
            length,
          });
          return { ...d, display_name };
        })
      : [
          ...splitData.normal,
          {
            name: "Others",
            display_name: "Others",
            value: splitData.reduced.reduce((val, item) => val + item.value, 0),
          },
        ];

  const ListItem = ({
    d,
    hexColorString,
  }: {
    d: IChartGroup;
    hexColorString: string;
  }) => (
    <li
      className={mergeClass(
        "flex w-full min-w-[16rem] py-0.5 gap-2",
        "hover:bg-black/10 rounded text-sm relative group",
        { "cursor-pointer": d.link }
      )}
      onMouseEnter={() => setHighlight(d.name)}
      onMouseLeave={() => setHighlight(null)}
    >
      <div
        className={mergeClass("flex-grow flex gap-2 items-center w-full", {
          "group-hover:underline": d.link,
        })}
      >
        <div
          className="w-3 h-3 rounded-full relative text-nowrap"
          style={{ background: hexColorString }}
        >
          <div className="w-3 h-3 rounded-full absolute top-0 left-0 opacity-0 group-hover:opacity-100 bg-[#888888]" />
        </div>
        {d.display_name}
      </div>
      <div className="font-bold text-right min-w-[2rem] px-2">{d.value}</div>
      {d.name.length > length && (
        <div
          className={mergeClass(
            "absolute -top-0.5 -left-0.5 flex px-3 py-0.5 rounded border bg-gray-200",
            "w-max min-w-full gap-4 z-10 shadow pointer-events-none delay-500",
            "transition opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto"
          )}
        >
          <div className="text-nowrap flex-grow underline">
            {removeTags(removeNbsp(d.name))}
          </div>
          <div className="font-bold text-right">{d.value}</div>
        </div>
      )}
    </li>
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-10 py-2">
        <ul className="flex flex-col justify-center min-w-[16rem] gap-0.5">
          {useThisData.map((d: IChartGroup, index) =>
            d.link ? (
              <Link to={d.link} target="_blank" key={index}>
                <ListItem
                  d={d}
                  hexColorString={chartColors[index % chartColors.length]}
                />
              </Link>
            ) : (
              <ListItem
                d={d}
                key={index}
                hexColorString={chartColors[index % chartColors.length]}
              />
            )
          )}
          {/* {hasReducedData && (
            <li
              className={mergeClass(
                "w-full underline text-xs py-1 px-3",
                "cursor-pointer hover:bg-black/10 rounded"
              )}
              onClick={() => setShowReduced(!showReduced)}
            >
              {showReduced ? `Show all` : `Show less`}
            </li>
          )} */}
        </ul>
        <div
          id="pie_chart_container"
          className={`flex-grow min-w-[540px] min-h-[280px]`}
        >
          <ResponsiveContainer width={"100%"} height={"100%"}>
            <PieChart>
              <Pie
                data={useThisData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={"75%"}
                fill={hlColor}
                label={Label}
                isAnimationActive={false}
              >
                {useThisData.map((entry, index) => {
                  const color =
                    entry.name === highlight
                      ? "#888888"
                      : chartColors[index % chartColors.length];
                  return <Cell key={`cell-${index}`} fill={color} />;
                })}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export { BasePieChart };
